import { isArray } from 'lodash';
import { publish } from 'pubsub-js';

import { PUBSUB_TOPICS } from '../../hooks';
import DateFormat, { Formats } from '../../utils/date';
import { NotificationType, sendNotification } from '../../utils/toast';
import {
  DISABLE_DRAFT_ORDER_REJECTED,
  DISABLE_DRAFT_ORDER_REQUESTED,
  DISABLE_DRAFT_ORDER_RESOLVED,
  DRAFT_ORDER_SEND_CART_REJECTED,
  DRAFT_ORDER_SEND_CART_REQUESTED,
  DRAFT_ORDER_SEND_CART_RESOLVED,
  GET_DRAFT_ORDERS_REJECTED,
  GET_DRAFT_ORDERS_REQUESTED,
  GET_DRAFT_ORDERS_RESOLVED,
  GET_SINGLE_DRAFT_ORDER_REJECTED,
  GET_SINGLE_DRAFT_ORDER_REQUESTED,
  GET_SINGLE_DRAFT_ORDER_RESOLVED,
} from '../actions/types';
import { Status } from '../models/europrisme';

const initialState = {
  status: Status.IDLE,
  loaded: false,
  message: null,
  comment: '',
  draftOrders: {},
  draftOrderCounts: {},
};

export default (state = initialState, action) => {
  switch (action.type) {
    // ========================================================================
    // Get all draft orders
    // ========================================================================
    case GET_DRAFT_ORDERS_REQUESTED: {
      return { ...state, loaded: false, status: Status.LOADING };
    }

    case GET_DRAFT_ORDERS_REJECTED: {
      const { error } = action.payload;

      const message = error.originalData
        ? error.originalData?.xmlst?.Message
        : error.message;

      sendNotification({ title: message, type: NotificationType.ERROR });

      return {
        ...state,
        loaded: true,
        status: Status.ERROR,
        message,
      };
    }

    case GET_DRAFT_ORDERS_RESOLVED: {
      const { data } = action.payload;
      const { xmlst } = data;
      const { Item: recordsDraftOrders } = xmlst;

      let draftOrdersList = [];
      if (recordsDraftOrders) {
        if (isArray(recordsDraftOrders)) {
          draftOrdersList = recordsDraftOrders;
        } else {
          draftOrdersList = [recordsDraftOrders];
        }
      }

      const draftOrders = draftOrdersList.reduce(
        (acc, { kIDDraftOrder, dCreationDate, ...rest }) => {
          const date = DateFormat.parse(dCreationDate, Formats.PREORDERS_API);
          const formattedDate = DateFormat.format(date, Formats.DAY_MONTH_YEAR);

          acc[kIDDraftOrder] = {
            code: kIDDraftOrder,
            date,
            formattedDate,
            status: Status.IDLE,
            ...rest,
          };

          return acc;
        },
        {},
      );

      return {
        ...state,
        loaded: true,
        status: Status.IDLE,
        draftOrders,
      };
    }

    // ========================================================================
    // Get single draft order
    // ========================================================================
    case GET_SINGLE_DRAFT_ORDER_REQUESTED: {
      const { params } = action.payload;
      const { sIDDraftOrder } = params;

      const previousData = state.draftOrders[sIDDraftOrder] || {};

      const draftOrder = {
        ...previousData,
        status: Status.LOADING,
      };

      return {
        ...state,
        draftOrders: {
          ...state.draftOrders,
          [sIDDraftOrder]: draftOrder,
        },
      };
    }

    case GET_SINGLE_DRAFT_ORDER_REJECTED: {
      const { error, params } = action.payload;
      const { sIDDraftOrder } = params;

      const message = error.originalData
        ? error.originalData.Message
        : error.message;

      sendNotification({ title: message, type: NotificationType.ERROR });

      const previousData = state.draftOrders[sIDDraftOrder];

      if (!previousData) return state;

      const draftOrder = {
        ...previousData,
        status: Status.ERROR,
        message,
      };

      return {
        ...state,
        loaded: true,
        draftOrders: {
          ...state.draftOrders,
          [sIDDraftOrder]: draftOrder,
        },
      };
    }

    case GET_SINGLE_DRAFT_ORDER_RESOLVED: {
      const { data, params } = action.payload;
      const { sIDDraftOrder } = params;
      const { xmlst } = data;
      const { Success, Message, ...rawData } = xmlst;

      const previousData = state.draftOrders[sIDDraftOrder] || {};

      const { Item, ...recordDataRest } = rawData;
      const recordData = {
        ...recordDataRest,
        records: isArray(Item) ? Item : [Item],
      };

      const date = DateFormat.parse(
        recordData.dCreationDate,
        Formats.PREORDERS_API,
      );

      const draftOrder = {
        ...previousData,
        ...recordData,
        code: recordData.kIDDraftOrder,
        status: Status.IDLE,
        date,
        formattedDate: DateFormat.format(date, Formats.DAY_MONTH_YEAR),
      };

      return {
        ...state,
        loaded: true,
        draftOrders: {
          ...state.draftOrders,
          [sIDDraftOrder]: draftOrder,
        },
      };
    }

    // ========================================================================
    // Send cart as single draft order
    // ========================================================================
    case DRAFT_ORDER_SEND_CART_REQUESTED: {
      return { ...state, status: Status.LOADING };
    }

    case DRAFT_ORDER_SEND_CART_RESOLVED: {
      const { data } = action.payload;

      publish(PUBSUB_TOPICS.SEND_DRAFT_ORDER_SUCCESS);

      return {
        ...state,
        status: Status.IDLE,
        preorderInfo: data,
      };
    }

    case DRAFT_ORDER_SEND_CART_REJECTED: {
      const { error } = action.payload;

      const message = error.originalData
        ? error.originalData.Message
        : error.message;

      sendNotification({ title: message, type: NotificationType.ERROR });

      return {
        ...state,
        status: Status.ERROR,
        message,
      };
    }

    // ========================================================================
    // Disable draft order
    // ========================================================================
    case DISABLE_DRAFT_ORDER_REQUESTED: {
      return { ...state, loaded: false, status: Status.LOADING };
    }

    case DISABLE_DRAFT_ORDER_REJECTED: {
      const { error } = action.payload;

      const message = error.originalData
        ? error.originalData?.xmlst?.Message
        : error.message;

      sendNotification({ title: message, type: NotificationType.ERROR });

      return {
        ...state,
        loaded: true,
        status: Status.ERROR,
        message,
      };
    }

    case DISABLE_DRAFT_ORDER_RESOLVED: {
      const { data, fullParams } = action.payload;
      const { Message: message } = data.xmlst;
      const { sIDDraftOrder: draftOrderID } = fullParams;

      const { draftOrders } = state;

      if (message) {
        sendNotification({ title: message, type: NotificationType.SUCCESS });
      }

      const filteredDraftOrders = Object.fromEntries(
        Object.entries(draftOrders).filter(([key]) => {
          return key !== draftOrderID;
        }),
      );

      return {
        ...state,
        loaded: true,
        status: Status.IDLE,
        draftOrders: filteredDraftOrders,
      };
    }

    default:
      return state;
  }
};
